export type SerializedPushSubscription = ReturnType<PushSubscription["toJSON"]>;

type SubscribeOptions = {
  vapidPublicKey: string;
  createSubscription: (
    subscription: SerializedPushSubscription
  ) => Promise<void>;
};

function vapidKeyToBytes(value: string): ArrayBuffer {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const normalized = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) {
    bytes[index] = raw.charCodeAt(index);
  }
  return bytes.buffer;
}

export async function subscribeToPush({
  vapidPublicKey,
  createSubscription,
}: SubscribeOptions): Promise<PushSubscription> {
  if (!vapidPublicKey) throw new Error("Push notifications are not configured");
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("Push notifications are not supported by this browser");
  }
  if ((await Notification.requestPermission()) !== "granted") {
    throw new Error("Notification permission was not granted");
  }
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: vapidKeyToBytes(vapidPublicKey),
  });
  await createSubscription(subscription.toJSON());
  return subscription;
}

export async function unsubscribeFromPush(
  subscription: PushSubscription | null,
  removeSubscription: () => Promise<void>
): Promise<void> {
  if (!subscription) return;
  await subscription.unsubscribe();
  await removeSubscription();
}
